import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DollarSign, FileText, MessageCircle, Plus, Filter, Search, Download, Eye, Send, Phone, Mail, Bell, Upload, CheckCircle, AlertCircle, X } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { formatDate } from '../../utils/formatters.js';
import { toast } from 'react-hot-toast';
import { payslipsAPI, usersAPI } from '../../lib/api.js';

// Helper function to format phone number for display (remove 91 prefix if present)
const formatPhoneForDisplay = (phoneNumber) => {
  if (!phoneNumber) return '';
  const phoneStr = String(phoneNumber).trim();
  // If phone starts with 91 and has 12 digits, remove the 91 prefix
  if (phoneStr.startsWith('91') && phoneStr.length === 12) {
    return phoneStr.substring(2);
  }
  return phoneStr;
};

function SalaryFinancePage() {
  const [activeTab, setActiveTab] = useState('payslips');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedPayslips, setSelectedPayslips] = useState(new Set());
  const [payslipFilters, setPayslipFilters] = useState({
    // status: '',
    month_year: '',
    search: ''
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [uploadResult, setUploadResult] = useState(null);
  // PDF import is a two-step flow: preview (dry run) → confirm. Holds the picked
  // File + the preview breakdown while the admin reviews before committing.
  const [pdfPreview, setPdfPreview] = useState(null);
  const [isBusy, setIsBusy] = useState(false);

  const queryClient = useQueryClient();

  // API Queries
  const { data: payslipsData, isLoading: payslipsLoading, error: payslipsError } = useQuery({
    queryKey: ['payslips', payslipFilters, pagination.page, pagination.limit],
    queryFn: () => {
      // Always use pagination from state - backend will handle pagination
      // Client-side filtering will filter the results if search/month is active
      return payslipsAPI.getAll({ 
        ...payslipFilters, 
        page: pagination.page, 
        limit: pagination.limit 
      });
    },
    // Removed refetchInterval to prevent unnecessary API calls
    // Data will refetch when filters change or queries are invalidated
    // refetchInterval: 30000,
  });


  const { data: payslipStats, isLoading: statsLoading } = useQuery({
    queryKey: ['payslip-stats', selectedMonth],
    queryFn: () => payslipsAPI.getStats({ month_year: selectedMonth }),
    // Removed refetchInterval to prevent unnecessary API calls
    // refetchInterval: 60000,
  });

  const { data: eligibleUsers, isLoading: usersLoading } = useQuery({
    queryKey: ['eligible-users', selectedMonth],
    queryFn: () => payslipsAPI.getEligibleUsers({ month_year: selectedMonth }),
    enabled: showPayslipModal,
  });

  // Mutations
  const sendPayslipMutation = useMutation({
    mutationFn: (payslipId) => payslipsAPI.sendViaWhatsApp(payslipId),
    onSuccess: () => {
      queryClient.invalidateQueries(['payslips']);
      queryClient.invalidateQueries(['payslip-stats']);
    },
  });

  const bulkSendMutation = useMutation({
    mutationFn: (payslipIds) => payslipsAPI.bulkSendViaWhatsApp({ payslip_ids: payslipIds }),
    onSuccess: () => {
      queryClient.invalidateQueries(['payslips']);
      queryClient.invalidateQueries(['payslip-stats']);
      setSelectedPayslips(new Set());
    },
  });

  // Handler functions
  const handleFilterChange = (key, value) => {
    setPayslipFilters(prev => ({ ...prev, [key]: value }));
    if (key === 'search') {
      // when searching, reset to first page for consistency
      setPagination(prev => ({ ...prev, page: 1 }));
    }
    if (key === 'month_year') {
      // keep top stats/calendar in sync and reset to first page
      setSelectedMonth(value);
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  };

  const handleSelectPayslip = (payslipId) => {
    const newSelected = new Set(selectedPayslips);
    if (newSelected.has(payslipId)) {
      newSelected.delete(payslipId);
    } else {
      newSelected.add(payslipId);
    }
    setSelectedPayslips(newSelected);
  };

  const handleSelectAllPayslips = () => {
    const payslips = payslipsData?.data?.data?.payslips || [];
    if (selectedPayslips.size === payslips.length) {
      setSelectedPayslips(new Set());
    } else {
      setSelectedPayslips(new Set(payslips.map(p => p.id)));
    }
  };

  const handleSendPayslip = (payslipId) => {
    if (window.confirm('Send this payslip via WhatsApp?')) {
      sendPayslipMutation.mutate(payslipId);
    }
  };

  const handleBulkSend = () => {
    if (selectedPayslips.size === 0) return;

    const confirmMessage = `Send ${selectedPayslips.size} payslip(s) via WhatsApp?`;
    if (window.confirm(confirmMessage)) {
      bulkSendMutation.mutate(Array.from(selectedPayslips));
    }
  };

  // Download payslip using axios (for Node.js or advanced browser use)
  // This function is not used in the UI, but shows how to use axios for file download.
  // For browser download, see handleDownloadPayslip below.
  // Example usage (Node.js): node downloadPayslip.js
  // const axios = require('axios');
  // let config = {
  //   method: 'get',
  //   maxBodyLength: Infinity,
  //   url: 'http://localhost:4000/salary-slip/download/bf5e04ae0a574b5edc822f08958bb697',
  //   headers: { 
  //     'Authorization': 'Bearer <your_token_here>'
  //   },
  //   responseType: 'arraybuffer'
  // };
  // axios.request(config)
  // .then((response) => {
  //   // Save file logic here, e.g. fs.writeFileSync('payslip.pdf', response.data);
  //   console.log('Payslip downloaded');
  // })
  // .catch((error) => {
  //   console.log(error);
  // });

  const handleDownloadPayslip = (download_url) => {
    const token = localStorage.getItem('srpf_auth_token');
    if (!token) {
      alert('You must be logged in to download payslips.');
      return;
    }
    fetch(download_url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('Failed to download payslip');
        }
        // Get blob and trigger download as PDF
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Try to extract filename from Content-Disposition header
        const disposition = response.headers.get('Content-Disposition');
        let filename = 'payslip.pdf';
        if (disposition && disposition.includes('filename=')) {
          filename = disposition.split('filename=')[1].replace(/"/g, '').trim();
          // Ensure .pdf extension
          if (!filename.toLowerCase().endsWith('.pdf')) {
            filename = filename.replace(/\.[^/.]+$/, '') + '.pdf';
          }
        }
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        alert(err.message || 'Error downloading payslip');
      });
  };




// const handleFileUpload = (event) => {
//   const files = event.target.files;
//   if (!files || files.length === 0) return;
//   setUploadResult(null);
//   const formData = new FormData();
//   Array.from(files).forEach((file) => {
//     formData.append('htmlFiles', file);
//   });

//   const token = localStorage.getItem('srpf_auth_token');
//   payslipsAPI.uploadHtml(formData, token)
//     .then((result) => {
//       setUploadResult(result?.data || result); // Save API response for UI
//       queryClient.invalidateQueries(['payslips']);
//     })
//     .catch((err) => {
//       setUploadResult({
//         success: false,
//         message: err?.message || 'Upload failed',
//         processed: { successful: 0, failed: files.length, failures: [] },
//       });
//     });
// };



  // Get status badge variant
  
  // Actually commit an import (HTML directly, or PDF after the admin confirms).
  const runImport = (fileArr, isPdf) => {
    const token = localStorage.getItem('srpf_auth_token');
    if (!token) {
      toast.error('❌ Missing authentication token. Please log in again.');
      return;
    }
    setUploadResult(null);
    const fieldName = isPdf ? 'pdfFiles' : 'htmlFiles';
    const uploadFn = isPdf ? payslipsAPI.uploadPdf : payslipsAPI.uploadHtml;
    const formData = new FormData();
    fileArr.forEach((file) => formData.append(fieldName, file));

    const toastId = toast.loading(`📤 Importing ${fileArr.length} file${fileArr.length > 1 ? 's' : ''}...`);
    setIsBusy(true);
    uploadFn(formData, token)
      .then((result) => {
        setUploadResult(result?.data || result);
        setPdfPreview(null);
        queryClient.invalidateQueries(['payslips']);
        toast.success('✅ Salary slips imported successfully!', { id: toastId });
      })
      .catch((err) => {
        const message = err?.response?.data?.message || err?.message || 'Import failed';
        setUploadResult({
          success: false,
          message,
          processed: { successful: 0, failed: fileArr.length, failures: [] },
        });
        toast.error(`❌ ${message}`, { id: toastId });
      })
      .finally(() => {
        setIsBusy(false);
        toast.dismiss(toastId);
      });
  };

  const handleFileUpload = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      toast.error('⚠️ Please select at least one file.');
      return;
    }
    const fileArr = Array.from(files);
    // The customer switched from combined HTML to a single combined PDF. Route by
    // file type; don't mix the two in one upload.
    const isPdf = fileArr.some((f) => f.name.toLowerCase().endsWith('.pdf'));
    const isHtml = fileArr.some((f) => /\.html?$/i.test(f.name));
    // Clear the input so re-selecting the same file fires onChange again.
    event.target.value = '';
    if (isPdf && isHtml) {
      toast.error('⚠️ Please upload either PDF or HTML files, not both at once.');
      return;
    }
    setUploadResult(null);
    setPdfPreview(null);

    // HTML keeps the direct one-step import.
    if (!isPdf) {
      runImport(fileArr, false);
      return;
    }

    // PDF → dry run first, then let the admin confirm before committing.
    const token = localStorage.getItem('srpf_auth_token');
    if (!token) {
      toast.error('❌ Missing authentication token. Please log in again.');
      return;
    }
    const file = fileArr[0]; // a single combined PDF
    const formData = new FormData();
    formData.append('pdfFiles', file);
    const toastId = toast.loading('🔍 Analyzing PDF — matching against employees...');
    setIsBusy(true);
    payslipsAPI.previewPdf(formData, token)
      .then((result) => {
        setPdfPreview({ file, data: result?.data || result });
        toast.success('✅ Preview ready — review, then confirm.', { id: toastId });
      })
      .catch((err) => {
        const message = err?.response?.data?.error || err?.message || 'Preview failed';
        toast.error(`❌ ${message}`, { id: toastId });
      })
      .finally(() => {
        setIsBusy(false);
        toast.dismiss(toastId);
      });
  };
  
  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'generated': return 'warning';
      case 'sent': return 'info';
      case 'delivered': return 'success';
      case 'failed': return 'danger';
      default: return 'default';
    }
  };

  const tabs = [
    { id: 'payslips', name: 'Payslips', icon: FileText },
    { id: 'whatsapp', name: 'WhatsApp Integration', icon: MessageCircle },
  ];

  // Extract data from API responses
  const payslips = payslipsData?.data?.data?.payslips || [];
  const stats = payslipStats?.data?.data || {
    total_payslips: 0,
    sent: 0,
    delivered: 0,
    failed: 0,
    delivery_rate: 0
  };

  // Loading state
  if (payslipsLoading && statsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-[1600px] mx-auto px-6 py-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              ))}
            </div>
            <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  // Only render the main HTML if handleFileUpload is defined (truthy)
  if (!handleFileUpload) return null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className=" mx-auto px-6 py-6">

          {pdfPreview && (
            <div className="mb-6 rounded-lg border border-blue-400 bg-blue-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="text-blue-600" size={20} />
                <span className="font-semibold text-blue-800">
                  Preview — nothing imported yet
                </span>
              </div>
              <div className="mb-3 text-sm text-gray-700">
                <b className="font-mono">{pdfPreview.file?.name}</b> — {pdfPreview.data?.message}
              </div>
              {pdfPreview.data?.summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-gray-700 mb-3">
                  <div className="rounded bg-white border p-2">
                    <div className="text-lg font-bold text-gray-900">{pdfPreview.data.summary.uniqueEmployees}</div>
                    employees in PDF
                  </div>
                  <div className="rounded bg-white border p-2">
                    <div className="text-lg font-bold text-green-700">{pdfPreview.data.summary.wouldStore}</div>
                    will import
                  </div>
                  <div className="rounded bg-white border p-2">
                    <div className="text-lg font-bold text-amber-600">{pdfPreview.data.summary.wouldSkipDuplicate}</div>
                    already imported
                  </div>
                  <div className="rounded bg-white border p-2">
                    <div className="text-lg font-bold text-red-700">{pdfPreview.data.summary.wouldFail}</div>
                    no matching user
                  </div>
                  <div className="col-span-2 sm:col-span-4 text-gray-500">
                    Salary month: <b>{pdfPreview.data.summary.salaryMonths?.join(', ') || '—'}</b>
                  </div>
                </div>
              )}
              {pdfPreview.data?.unmatched?.length > 0 && (
                <details className="text-xs mb-3">
                  <summary className="cursor-pointer font-medium text-red-700">
                    Show employees with no matching user ({pdfPreview.data.unmatched.length}) — these will be skipped
                  </summary>
                  <div className="overflow-x-auto mt-2 max-h-64 border rounded bg-white">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="bg-red-100">
                          <th className="px-2 py-1 text-left font-semibold text-red-800">DGP ID</th>
                          <th className="px-2 py-1 text-left font-semibold text-red-800">Name</th>
                          <th className="px-2 py-1 text-left font-semibold text-red-800">Page</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pdfPreview.data.unmatched.map((u, idx) => (
                          <tr key={u.empId + idx} className="border-b last:border-b-0">
                            <td className="px-2 py-1 font-mono">{u.empId}</td>
                            <td className="px-2 py-1">{u.name}</td>
                            <td className="px-2 py-1">{u.page}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={isBusy || !(pdfPreview.data?.summary?.wouldStore > 0)}
                  onClick={() => runImport([pdfPreview.file], true)}
                  className="px-3 py-2 rounded-lg bg-teal-700 text-white font-medium text-sm disabled:opacity-50"
                >
                  Confirm import ({pdfPreview.data?.summary?.wouldStore ?? 0})
                </button>
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => setPdfPreview(null)}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {uploadResult && (
            <div className={`mb-6 rounded-lg border ${uploadResult.success ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'} p-4`}>
              <div className="flex items-center gap-2 mb-2">
                {uploadResult.success ? (
                  <CheckCircle className="text-green-600" size={20} />
                ) : (
                  <AlertCircle className="text-red-600" size={20} />
                )}
                <span className={`font-semibold ${uploadResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {uploadResult.success ? 'Upload Successful' : 'Upload Failed'}
                </span>
              </div>
              <div className="mb-2 text-sm text-gray-700">{uploadResult.message}</div>
              {uploadResult.processed && (
                <div className="mb-2 text-xs text-gray-600">
                  <span className="mr-4">
                    <b>Successful:</b> {uploadResult.processed.successful}
                  </span>
                  <span>
                    <b>Failed:</b> {uploadResult.processed.failed}
                  </span>
                </div>
              )}
              {uploadResult.processed?.failures?.length > 0 && (
                <div className="mt-2">
                  <details className="text-xs">
                    <summary className="cursor-pointer font-medium text-red-700">
                      Show failed files ({uploadResult.processed.failures.length})
                    </summary>
                    <div className="overflow-x-auto mt-2 max-h-64 border rounded bg-white">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="bg-red-100">
                            <th className="px-2 py-1 text-left font-semibold text-red-800">Filename</th>
                            <th className="px-2 py-1 text-left font-semibold text-red-800">Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadResult.processed.failures.slice(0, 100).map((fail, idx) => (
                            <tr key={fail.filename + idx} className="border-b last:border-b-0">
                              <td className="px-2 py-1 font-mono">{fail.filename}</td>
                              <td className="px-2 py-1 text-red-700">{fail.error}</td>
                            </tr>
                          ))}
                          {uploadResult.processed.failures.length > 100 && (
                            <tr>
                              <td colSpan={2} className="px-2 py-1 text-center text-gray-500">
                                ...and {uploadResult.processed.failures.length - 100} more
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </div>
              )}
            </div>
          )}

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
              Salary & Financial Awareness
            </h1>
            <Badge variant="secondary" className="text-xs">
              {payslipsData?.data?.total} payslips
            </Badge>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <Plus className="h-4 w-4 text-teal-700" />
            <input
              type="file"
              className="hidden"
              accept=".pdf,.htm,.html"
            onChange={handleFileUpload}
            multiple
            />
            <span className="px-3 py-2 border border-teal-700 rounded-lg bg-teal-50 text-teal-700 font-medium text-sm">
              Upload Salary Slips
            </span>
          </label>

        </div>

        {/* Content without boxy card backgrounds */}
        <div className="">
          <div className="space-y-6">

            {/* Search and filters - inline, no card */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    placeholder="Search by phone, DGP ID, name, badge or file name (e.g. DGPTBSF8701 / 9876543210 / TEJASWI)"
                    value={payslipFilters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    icon={<Search size={16} />}
                    className="w-full"
                  />
                </div>
                <div className="flex gap-2 items-center">
                  {/* <select
                    value={payslipFilters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="">All Status</option>
                    <option value="generated">Generated</option>
                    <option value="sent">Sent</option>
                    <option value="delivered">Delivered</option>
                    <option value="failed">Failed</option>
                  </select> */}
                  <div className="flex items-center gap-2">
                    <input
                      type="month"
                      value={payslipFilters.month_year}
                      onChange={(e) => handleFilterChange('month_year', e.target.value)}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                    {payslipFilters.month_year && (
                      <button
                        onClick={() => handleFilterChange('month_year', '')}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                        title="Clear month filter"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div> 
              </div>

              {selectedPayslips.size > 0 && (
                <div className="flex items-center justify-between p-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-teal-700" />
                    <span className="font-medium text-teal-800 dark:text-teal-200">
                      {selectedPayslips.size} payslip{selectedPayslips.size !== 1 ? 's' : ''} selected
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleBulkSend}
                      disabled={bulkSendMutation.isPending}
                    >
                      <Send size={16} className="mr-1" />
                      Send via WhatsApp
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedPayslips(new Set())}
                    >
                      <X size={16} />
                      Clear
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Payslips Table */}
                  {payslipsLoading ? (
                    <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                    </div>
                  ) : payslipsError ? (
                    <div className="text-center py-8">
                    <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Error loading payslips
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {payslipsError.message}
                    </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                    <table className="w-full border border-teal-700 ">
                      <thead>
                      <tr className="bg-teal-700 border-b border-teal-600 dark:border-teal-600 divide-x divide-teal-600 dark:divide-teal-600">
                        <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                        <button
                          onClick={handleSelectAllPayslips}
                          className="flex items-center justify-center w-4 h-4 text-white hover:text-teal-200 transition-colors"
                        >
                          {selectedPayslips.size === payslips.length && payslips.length > 0 ? (
                          <CheckCircle size={16} />
                          ) : (
                          <div className="w-4 h-4 border border-white rounded"></div>
                          )}
                        </button>
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                        Phone Number
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                        Payslip File
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                        Payslip Month
                        </th>
                         <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                        Date Of Joining
                        </th>
                         <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                        Date of Retirement
                        </th>
                         <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                        Date of Birth
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                        WhatsApp
                        </th>
                        <th className="px-2 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                        Actions
                        </th>
                      </tr>
                      </thead>
                  <tbody>
                      {(() => {
                        // Server-side search now handles phone / DGP ID / name /
                        // badge / file name. Client-side filter removed because
                        // it only filtered the current page, hiding hits that
                        // lived on other pages.
                        const files = payslipsData?.data?.files || [];
                        return files.map((file) => (
                        <tr
                        key={file.file_name}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-800 border-b border-teal-600 dark:border-teal-600 divide-x divide-teal-600 dark:divide-teal-600 ${selectedPayslips.has(file.file_name) ? 'bg-teal-50 dark:bg-teal-900/20' : ''
                          }`}
                        >
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                          onClick={() => handleSelectPayslip(file.file_name)}
                          className="flex items-center justify-center w-4 h-4 text-teal-700 hover:text-teal-800 transition-colors"
                          >
                          {selectedPayslips.has(file.file_name) ? (
                            <CheckCircle size={16} />
                          ) : (
                            <div className="w-4 h-4 border border-teal-700 rounded"></div>
                          )}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {file.user.file_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {formatPhoneForDisplay(file.user?.phone_number)}
                          </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-900 dark:text-white">
                          {file.file_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                           {file.salary_month}
                          </div>
                        </td>
                         <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                           {file.date_of_joining ? formatDate(file.date_of_joining) : ''}
                          </div>
                        </td>
                         <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                           {file.date_of_retirement ? formatDate(file.date_of_retirement) : ''}
                          </div>
                        </td>
                         <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                           {file.date_of_birth ? formatDate(file.date_of_birth) : ''}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                          {file.user?.whatsapp_enabled ? (
                            <Badge variant="success">Enabled</Badge>
                          ) : (
                            <Badge variant="default">Disabled</Badge>
                          )}
                          {file.user?.whatsapp_language && (
                            <span className="text-xs text-gray-500">
                            {file.user.whatsapp_language.toUpperCase()}
                            </span>
                          )}
                          </div>
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-center text-sm font-medium">
                          <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadPayslip(file.download_url)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            title="Download Payslip"
                          >
                            <Download size={16} />
                          </Button>
                          {/* <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSendPayslip(file.file_name)}
                            disabled={!file.user?.whatsapp_enabled || sendPayslipMutation.isPending}
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 border-green-600"
                            title="Send via WhatsApp"
                          >
                            <Send size={16} />
                            Send
                          </Button> */}
                          </div>
                        </td>
                      </tr>
                    ));
                      })()}
                  </tbody>
                </table>
                {/* Pagination Footer */}
                <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mt-3">
                  <div>
                    {payslipsData?.data?.total > 0 && (
                      <span>
                        {`Showing ${Math.min((pagination.page - 1) * pagination.limit + 1, payslipsData.data.total)} to ${Math.min(pagination.page * pagination.limit, payslipsData.data.total)} of ${payslipsData.data.total} payslips`}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className={`px-3 py-1 rounded border ${pagination.page > 1 ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : 'opacity-50 cursor-not-allowed'}`}
                      onClick={() => pagination.page > 1 && setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page <= 1}
                    >
                      Previous
                    </button>
                    <span>
                      Page {payslipsData?.data?.page || pagination.page} of {payslipsData?.data?.totalPages || 1}
                    </span>
                    <button
                      className={`px-3 py-1 rounded border ${(payslipsData?.data?.page || 1) < (payslipsData?.data?.totalPages || 1) ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : 'opacity-50 cursor-not-allowed'}`}
                      onClick={() => ((payslipsData?.data?.page || 1) < (payslipsData?.data?.totalPages || 1)) && setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={!payslipsData || (payslipsData?.data?.page || 1) >= (payslipsData?.data?.totalPages || 1)}
                    >
                      Next
                    </button>
                    <select
                      className="ml-2 border rounded px-2 py-1"
                      value={pagination.limit}
                      onChange={(e) => { setPagination({ page: 1, limit: Number(e.target.value) }); }}
                    >
                      <option value={10}>10/page</option>
                      <option value={20}>20/page</option>
                      <option value={50}>50/page</option>
                    </select>
                  </div>
                </div>
                {(payslipsData?.data?.files || []).length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <FileText className="mx-auto h-12 w-12 mb-3" />
                    <p>No payslips found</p>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default SalaryFinancePage;
